import { Injectable, inject, NgZone, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { fromEvent, merge, Subscription, of } from 'rxjs';
import { throttleTime, filter, catchError, retry } from 'rxjs/operators';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { BrowserStorageService } from './browser-storage.service';
import { RefreshTokenService } from './refresh-token.service';
import { LogoutService } from '@app/services/logout.service';
import { EncryptionService } from './encryption.service';
import { SessionExpiredDialogComponent } from '@app/shared/components/session-expired-dialog/session-expired-dialog.component';
import { TranslocoService } from '@jsverse/transloco';

// --- CLAVES DE PERSISTENCIA ---
const SESSION_START_KEY = 'session_start_time';
const BACKEND_SYNC_KEY = 'last_backend_sync_time';

// --- CONFIGURACIÓN DE TIEMPOS (Prueba de 3 min) ---
const WARNING_TIME = 1500;          // 25 min para el modal
const MODAL_GRACE_PERIOD = 120;    // 2 min de espera en el modal
const LOGOUT_TIME = WARNING_TIME + MODAL_GRACE_PERIOD;
const BACKEND_SYNC_THRESHOLD = 600; // Sincronizar backend cada 600s si hay actividad

@Injectable({ providedIn: 'root' })
export class IdleTimerService implements OnDestroy {
  private storage = inject(BrowserStorageService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private zone = inject(NgZone);
  private refreshService = inject(RefreshTokenService);
  private router = inject(Router);
  private logoutService = inject(LogoutService);
  private encryption = inject(EncryptionService);
  private translocoService = inject(TranslocoService);

  private activitySubscription: Subscription | null = null;
  private visibilitySubscription: Subscription | null = null;
  private worker: Worker | null = null;
  private isModalOpen = false;
  private isOnLoginPage = false;
  private currentModalRef: MatDialogRef<SessionExpiredDialogComponent> | null = null;

  private hasWarningFired = false;          // Para evitar re-abrir el modal en el mismo ciclo

  constructor() {
    this.monitorRouteChanges();
    this.initWorker();
    this.initVisibilityListeners();
    // Aplicar lógica a la ruta actual (la navegación inicial ya pudo ocurrir antes de suscribirnos)
    setTimeout(() => this.applyCurrentRoute(), 0);
  }

  /**
   * Aplica start/stop según la URL actual y el token. Se llama al iniciar para no perder la navegación inicial.
   */
  private applyCurrentRoute(): void {
    const url = this.router.url;
    this.isOnLoginPage = url.includes('/login');
    const hasAccess = !!this.storage.getSessionStorageItem('access');
    if (this.isOnLoginPage) {
      this.stopWatching();
    } else if (hasAccess) {
      this.startWatching();
    }
  }

  /**
   * Evalúa el estado de la sesión basado en tiempo absoluto transcurrido
   * usando tiempos persistidos en sessionStorage para que sobrevivan
   * cambios de ruta y recargas de página.
   */
  private checkSessionStatus(): void {
    if (this.isOnLoginPage) {
      return;
    }

    const now = Date.now();

    // Leer tiempos persistidos; si no existen, asumir "ahora"
    const rawSessionStart = sessionStorage.getItem(SESSION_START_KEY);
    const sessionStartTime = rawSessionStart ? parseInt(rawSessionStart, 10) : now;

    const rawBackendSync = sessionStorage.getItem(BACKEND_SYNC_KEY);
    const backendSyncTime = rawBackendSync ? parseInt(rawBackendSync, 10) : now;

    const elapsedSeconds = Math.floor((now - sessionStartTime) / 1000);

    if (elapsedSeconds >= LOGOUT_TIME) {
      this.closeModalIfOpen();
      this.processLogout();
      return;
    }

    // Mostrar el modal SOLO una vez por ciclo de sesión (hasWarningFired)
    if (elapsedSeconds >= WARNING_TIME && !this.isModalOpen && !this.hasWarningFired) {
      this.hasWarningFired = true;
      this.handleInactivity();
    }
  }

  /**
   * Sincronización silenciosa con el backend (Evita el desfase)
   */
  private syncTokenWithBackend(): void {
    if (this.isModalOpen) return;

    const now = Date.now();
    const rawBackendSync = sessionStorage.getItem(BACKEND_SYNC_KEY);
    const backendSyncTime = rawBackendSync ? parseInt(rawBackendSync, 10) : now;
    const secondsSinceLastSync = Math.floor((now - backendSyncTime) / 1000);

    if (secondsSinceLastSync >= BACKEND_SYNC_THRESHOLD) {
      this.executeSilentRefresh();
    }
  }

  private executeSilentRefresh(): void {
    const refreshToken = sessionStorage.getItem('refresh');
    if (!refreshToken) return;

    const decryptedRefresh = this.encryption.decrypt(String(refreshToken));

    this.refreshService.refreshAccessToken(decryptedRefresh).pipe(
      retry({ count: 2, delay: 2000 }), // Reintento por fallos de red
      catchError(error => {
        this.zone.run(() => {
          this.snackBar.open(this.translocoService.translate('errors.session.connectionError'), this.translocoService.translate('common.accept'), {
            duration: 4000, 
            panelClass: ['warning-snackbar'] 
          });
        });
        return of(null);
      })
    ).subscribe((res: any) => {
      if (res?.access) {
        this.storage.setSessionStorageItem('access', this.encryption.encrypt(res.access));
        sessionStorage.setItem(BACKEND_SYNC_KEY, Date.now().toString());
      }
    });
  }

  public startWatching(): void {
    if (this.isOnLoginPage || this.isModalOpen) return;

    this.stopWatching();
    const now = Date.now();

    const existingSessionStart = sessionStorage.getItem(SESSION_START_KEY);
    const existingBackendSync = sessionStorage.getItem(BACKEND_SYNC_KEY);

    if (!existingSessionStart) {
      sessionStorage.setItem(SESSION_START_KEY, now.toString());
    }
    if (!existingBackendSync) {
      sessionStorage.setItem(BACKEND_SYNC_KEY, now.toString());
    }

    this.hasWarningFired = false;
    this.isModalOpen = false;

    this.activitySubscription = this.createActivityObservable().subscribe(() => {
      this.syncTokenWithBackend();
    });

    this.worker?.postMessage('start');
  }

  public stopWatching(): void {
    this.worker?.postMessage('stop');
    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe();
      this.activitySubscription = null;
    }
  }

  private createActivityObservable() {
    const events = [
      fromEvent(document, 'mousemove'), fromEvent(document, 'keydown'),
      fromEvent(document, 'touchstart'), fromEvent(document, 'scroll'), fromEvent(document, 'click')
    ];
    return merge(...events).pipe(throttleTime(2000));
  }

  private handleInactivity(): void {
    if (this.isModalOpen || this.isOnLoginPage) return;

    this.isModalOpen = true;

    this.zone.run(() => {
      this.currentModalRef = this.dialog.open(SessionExpiredDialogComponent, {
        width: '350px',
        disableClose: true,
        data: {
          title: this.translocoService.translate('errors.session.confirmationTitle'),
          message: this.translocoService.translate('errors.session.confirmationMessage'),
          timer: MODAL_GRACE_PERIOD 
        }
      });

      this.currentModalRef.afterClosed().subscribe((confirmed: boolean) => {
        this.isModalOpen = false;
        if (confirmed) {
          this.renewSession();
        } else {
          this.processLogout();
        }
      });
    });
  }

  private renewSession(): void {
    const refreshToken = sessionStorage.getItem('refresh');
    if (!refreshToken) return this.processLogout();

    try {
      const decryptedRefresh = this.encryption.decrypt(String(refreshToken));
      this.refreshService.refreshAccessToken(decryptedRefresh).subscribe({
        next: (response: any) => {
          if (response?.access) {
            this.storage.setSessionStorageItem('access', this.encryption.encrypt(response.access));

            // Forzar inicio de un nuevo ciclo de sesión (reloj absoluto + sync backend)
            const now = Date.now();
            sessionStorage.setItem(SESSION_START_KEY, now.toString());
            sessionStorage.setItem(BACKEND_SYNC_KEY, now.toString());

            this.startWatching(); // REINICIA AMBOS RELOJES (EL DE 25 MIN Y EL DE SYNC)
          } else {
            this.processLogout();
          }
        },
        error: () => this.processLogout()
      });
    } catch {
      this.processLogout();
    }
  }

  private initWorker() {
    if (typeof Worker === 'undefined') {
      setInterval(() => this.zone.run(() => this.checkSessionStatus()), 1000);
      return;
    }

    const workerCode = `
      let timer = null;
      self.onmessage = function(e) {
        if (e.data === 'start') {
          if (timer) clearInterval(timer);
          timer = setInterval(() => self.postMessage('tick'), 1000);
        } else if (e.data === 'stop') {
          clearInterval(timer);
          timer = null;
        }
      };
    `;

    const blob = new Blob([workerCode], { type: 'application/javascript' });
    this.worker = new Worker(URL.createObjectURL(blob));
    this.worker.onmessage = () => this.zone.run(() => this.checkSessionStatus());
  }

  // --- MÉTODOS DE SOPORTE (Visibilidad, Rutas, Logout) ---

  private initVisibilityListeners(): void {
    merge(fromEvent(document, 'visibilitychange'), fromEvent(window, 'focus'))
      .subscribe(() => this.checkSessionStatus());
  }

  private monitorRouteChanges(): void {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.isOnLoginPage = event.url.includes('/login');
        const hasAccess = !!this.storage.getSessionStorageItem('access');
        if (this.isOnLoginPage) {
          this.stopWatching();
        } else if (hasAccess) {
          this.startWatching();
        }
      });
  }

  private processLogout() {
    this.closeModalIfOpen();
    this.stopWatching();
    this.logoutService.logout();
    this.storage.clearSessionStorage();
    sessionStorage.clear();
    window.location.assign('/login');
  }

  private closeModalIfOpen(): void {
    if (this.currentModalRef) {
      this.currentModalRef.close(false);
      this.currentModalRef = null;
    }
    this.isModalOpen = false;
  }

  ngOnDestroy(): void {
    this.stopWatching();
    this.closeModalIfOpen();
  }
}